import { motion } from 'framer-motion';
import { useInView } from 'framer-motion';
import { useRef } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { CheckCircle, ArrowRight, Shield, Zap, Target, Users } from 'lucide-react';

const promises = [
  {
    icon: Target,
    title: 'On capture vos demandes',
    description: 'Tous vos canaux centralisés dans un système unique.',
  },
  {
    icon: Users,
    title: 'On ne perd plus personne',
    description: 'Suivi automatique et relances intelligentes.',
  },
  {
    icon: Zap,
    title: 'Vous savez quoi faire',
    description: 'Vision claire de vos priorités et actions à mener.',
  },
  {
    icon: Shield,
    title: 'Vous gardez le contrôle',
    description: 'Des systèmes que vous comprenez et maîtrisez.',
  },
];

export const PromiseSection = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-100px' });

  return (
    <section ref={ref} className="relative py-24 overflow-hidden">
      <div className="absolute inset-0 bg-gradient-hero" />
      <div className="hero-glow" />
      
      <div className="section-container relative z-10">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          {/* Left - Content */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.6 }}
          >
            <span className="section-label mb-4 inline-block">
              Notre promesse
            </span>
            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold text-foreground mb-6">
              Ce que Pulsia vous{' '}
              <span className="gradient-text">apporte</span>
            </h2>
            <p className="text-lg text-muted-foreground mb-8">
              Nous ne vendons pas des outils. Nous mettons en place des systèmes, 
              des processus et des résultats mesurables. Une réduction du chaos opérationnel.
            </p>

            {/* Promise List */}
            <div className="space-y-4 mb-8">
              {promises.map((promise, index) => (
                <motion.div
                  key={promise.title}
                  initial={{ opacity: 0, x: -20 }}
                  animate={isInView ? { opacity: 1, x: 0 } : {}}
                  transition={{ duration: 0.5, delay: 0.2 + index * 0.1 }}
                  className="flex items-start gap-4 p-4 rounded-xl bg-card/50 border border-border/50 hover:border-primary/30 transition-colors"
                >
                  <div className="feature-icon shrink-0">
                    <promise.icon className="w-5 h-5 text-primary" />
                  </div>
                  <div>
                    <h3 className="font-medium text-foreground mb-1">{promise.title}</h3>
                    <p className="text-sm text-muted-foreground">{promise.description}</p>
                  </div>
                </motion.div>
              ))}
            </div>

            <Link to="/accompagnements">
              <Button variant="hero" size="lg" className="btn-glow group">
                Découvrir nos accompagnements
                <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </Button>
            </Link>
          </motion.div>

          {/* Right - Visual */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="relative"
          >
            <div className="relative rounded-2xl overflow-hidden border border-border/50 shadow-lg">
              <img
                src="https://images.unsplash.com/photo-1694903089438-bf28d4697d9a?w=800&q=80"
                alt="Intelligence Artificielle Pulsia"
                className="w-full h-auto object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-background/80 via-transparent to-transparent" />
            </div>

            {/* Stats Card */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ delay: 0.8, duration: 0.6 }}
              className="absolute -bottom-6 left-6 right-6 p-6 rounded-xl glass-strong border border-border/50"
            >
              <div className="flex items-center justify-around">
                <div className="text-center">
                  <div className="flex items-center justify-center gap-1 mb-1">
                    <CheckCircle className="w-4 h-4 text-success" />
                    <span className="font-display text-2xl font-bold text-foreground">100%</span>
                  </div>
                  <p className="text-xs text-muted-foreground">Personnalisé</p>
                </div>
                <div className="w-px h-12 bg-border" />
                <div className="text-center">
                  <div className="flex items-center justify-center gap-1 mb-1">
                    <Zap className="w-4 h-4 text-primary" />
                    <span className="font-display text-2xl font-bold text-foreground">-70%</span>
                  </div>
                  <p className="text-xs text-muted-foreground">Tâches manuelles</p>
                </div>
                <div className="w-px h-12 bg-border" />
                <div className="text-center">
                  <div className="flex items-center justify-center gap-1 mb-1">
                    <Target className="w-4 h-4 text-accent" />
                    <span className="font-display text-2xl font-bold text-foreground">+40%</span>
                  </div>
                  <p className="text-xs text-muted-foreground">Conversions</p>
                </div>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};
